import React, { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import azkarData from "./data/azkar-data.json";
import hadithData from "./data/hadith-data.json";
import ZikrCard from "./components/ZikrCard";

type Mode = "morning" | "evening" | "private";

/**
 * Compute a triplet of hues from the current hour/minute. Lightness and
 * saturation are kept fixed in CSS so the dark text always retains contrast.
 * The hue rotates smoothly across the day, giving each time of day a
 * distinct mood without ever turning the surface dark.
 */
function computeHuesForNow(): { a: number; b: number; c: number } {
    const now = new Date();
    const h = now.getHours() + now.getMinutes() / 60;
    // Continuous rotation across 24h, offset so dawn lands in warm tones.
    const base = ((h / 24) * 360 + 200) % 360;
    return {
        a: base,
        b: (base + 50) % 360,
        c: (base + 110) % 360,
    };
}

function App() {
    const [azkar, setAzkar] = useState<{}[]>([]);
    const [active, setActive] = useState<Mode>("morning");
    const [dismissNotice, setDismissNotice] = useState(false);
    const [tiltEnabled, setTiltEnabled] = useState<boolean>(false);
    const [needsTiltPermission, setNeedsTiltPermission] = useState<boolean>(
        false
    );
    const rootRef = useRef<HTMLDivElement>(null);

    /* --- initial data load ------------------------------------------- */
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("mode") === "private") {
            setActive("private");
            setAzkar(hadithData as any);
        } else {
            setAzkar(azkarData?.morning as any);
        }
    }, []);

    /* --- time-of-day hue updates ------------------------------------- */
    useEffect(() => {
        const apply = () => {
            const { a, b, c } = computeHuesForNow();
            const root = document.documentElement;
            root.style.setProperty("--hue-a", a.toFixed(2));
            root.style.setProperty("--hue-b", b.toFixed(2));
            root.style.setProperty("--hue-c", c.toFixed(2));

            // Theme color for browser chrome
            const meta = document.querySelector('meta[name="theme-color"]');
            if (meta) {
                meta.setAttribute(
                    "content",
                    `hsl(${a.toFixed(0)}, 60%, 90%)`
                );
            }
        };
        apply();
        const id = window.setInterval(apply, 60_000);
        return () => window.clearInterval(id);
    }, []);

    /* --- tilt-driven gleam ------------------------------------------ */
    useEffect(() => {
        const root = document.documentElement;
        let raf = 0;
        let target = { x: 50, y: 50, angle: 135, strength: 0 };
        let current = { ...target };

        const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

        const tick = () => {
            current.x = lerp(current.x, target.x, 0.12);
            current.y = lerp(current.y, target.y, 0.12);
            current.angle = lerp(current.angle, target.angle, 0.12);
            current.strength = lerp(current.strength, target.strength, 0.1);
            root.style.setProperty("--tilt-x", `${current.x}%`);
            root.style.setProperty("--tilt-y", `${current.y}%`);
            root.style.setProperty("--tilt-angle", `${current.angle}`);
            root.style.setProperty(
                "--tilt-strength",
                current.strength.toFixed(3)
            );
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);

        const clamp = (v: number, lo: number, hi: number) =>
            Math.max(lo, Math.min(hi, v));

        const onOrient = (e: DeviceOrientationEvent) => {
            const gamma = e.gamma ?? 0; // -90..90 left/right
            const beta = e.beta ?? 0; // -180..180 front/back
            // Map ±35° to 0..100% tilt position
            const tx = clamp(50 + (gamma / 35) * 50, 0, 100);
            const ty = clamp(50 + (beta / 35) * 50, 0, 100);
            const angle = (gamma * 4 + beta * 1.2 + 135) % 360;
            const mag = Math.min(
                1,
                Math.sqrt(gamma * gamma + beta * beta) / 30
            );
            target = { x: tx, y: ty, angle, strength: mag };
        };

        const onPointer = (e: PointerEvent) => {
            const tx = (e.clientX / window.innerWidth) * 100;
            const ty = (e.clientY / window.innerHeight) * 100;
            const angle = (tx / 100) * 360;
            target = {
                x: tx,
                y: ty,
                angle,
                strength: 0.4,
            };
        };

        const ios = typeof (DeviceOrientationEvent as any)?.requestPermission ===
            "function";
        if (tiltEnabled || !ios) {
            window.addEventListener("deviceorientation", onOrient);
        }
        // pointer fallback for desktop / for visual idle on phones without sensor
        window.addEventListener("pointermove", onPointer);

        // gentle ambient sweep so cards still subtly gleam without input
        let ambient = 0;
        const ambientId = window.setInterval(() => {
            if (target.strength < 0.2) {
                ambient += 0.025;
                target = {
                    x: 50 + Math.sin(ambient) * 28,
                    y: 50 + Math.cos(ambient * 0.7) * 22,
                    angle: (ambient * 30) % 360,
                    strength: 0.18,
                };
            }
        }, 80);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("deviceorientation", onOrient);
            window.removeEventListener("pointermove", onPointer);
            window.clearInterval(ambientId);
        };
    }, [tiltEnabled]);

    useEffect(() => {
        const ios =
            typeof (DeviceOrientationEvent as any)?.requestPermission ===
            "function";
        setNeedsTiltPermission(ios && !tiltEnabled);
    }, [tiltEnabled]);

    const requestTilt = async () => {
        try {
            const fn = (DeviceOrientationEvent as any).requestPermission;
            if (typeof fn === "function") {
                const result = await fn();
                if (result === "granted") {
                    setTiltEnabled(true);
                    setNeedsTiltPermission(false);
                }
            } else {
                setTiltEnabled(true);
            }
        } catch {
            // ignore
        }
    };

    const switchTo = (mode: Mode) => {
        setActive(mode);
        if (mode === "morning") setAzkar(azkarData.morning as any);
        else if (mode === "evening") setAzkar(azkarData.evening as any);
        else setAzkar(hadithData as any);
    };

    const tabs: { id: Mode; label: string; sub: string }[] = useMemo(
        () => [
            { id: "morning", label: "صباح", sub: "morning" },
            { id: "evening", label: "مساء", sub: "evening" },
            { id: "private", label: "خاص", sub: "private" },
        ],
        []
    );

    const azkarMarkup = azkar.map((zikr: any, index) => (
        <div
            key={active + "-" + index + 1}
            className="animate-fade-rise"
            style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
        >
            <ZikrCard data={zikr} index={index} active={active} />
        </div>
    ));

    return (
        <div
            ref={rootRef}
            dir="rtl"
            className="relative h-screen w-screen overflow-hidden flex flex-col items-center font-sans text-slate-800"
        >
            <div className="dreamy-bg" aria-hidden />

            {!navigator.onLine && (
                <div className="w-full text-center py-1.5 text-xs font-medium bg-slate-800/80 text-white backdrop-blur-md">
                    أنت غير متصل بالإنترنت — التطبيق يعمل بدون اتصال
                </div>
            )}

            <div className="w-full max-w-xl flex flex-col h-full safe-top px-3">
                {/* Header */}
                <header className="mt-3 mb-2">
                    <div className="glass gleam rounded-3xl px-5 py-4 flex items-center justify-between">
                        <div className="flex items-baseline gap-2">
                            <h1 className="text-2xl font-bold tracking-tight">
                                <span
                                    className="bg-clip-text text-transparent"
                                    style={{
                                        backgroundImage:
                                            "linear-gradient(135deg, hsl(var(--hue-a),70%,40%), hsl(var(--hue-c),70%,45%))",
                                    }}
                                >
                                    azkar.moeabdalla.com
                                </span>
                            </h1>
                        </div>

                        {needsTiltPermission && (
                            <button
                                onClick={requestTilt}
                                className="tap-pop text-[11px] font-medium px-3 py-1.5 rounded-full bg-white/60 border border-white/70 text-slate-700 hover:bg-white/80 transition"
                            >
                                تفعيل اللمعان
                            </button>
                        )}
                    </div>
                </header>

                {/* First-run notice */}
                {!dismissNotice && !localStorage.localStorageNotice && (
                    <div className="glass gleam rounded-3xl px-4 py-3 mb-3 animate-fade-rise">
                        <p className="arabic text-sm text-slate-700 leading-relaxed mb-2 text-center">
                            يمكن للتطبيق الاحتفاظ بإدخالاتك على جهازك محليا
                        </p>
                        <div className="flex justify-center gap-2">
                            <button
                                onClick={() => setDismissNotice(true)}
                                className="tap-pop px-4 py-1.5 rounded-full text-sm bg-white/50 border border-white/70 text-slate-700 hover:bg-white/70 transition-all duration-300"
                            >
                                إخفاء
                            </button>
                            <button
                                onClick={() => {
                                    setDismissNotice(true);
                                    localStorage.setItem(
                                        "localStorageNotice",
                                        "true"
                                    );
                                }}
                                className="tap-pop px-4 py-1.5 rounded-full text-sm tab-pill-active text-slate-800 font-medium transition-all duration-300"
                            >
                                موافق
                            </button>
                        </div>
                    </div>
                )}

                {/* Segmented tab switcher */}
                <nav className="glass gleam rounded-full p-1.5 mb-3 relative">
                    <div className="grid grid-cols-3 relative">
                        {/* Sliding indicator */}
                        <div
                            className="absolute top-0 bottom-0 rounded-full tab-pill-active transition-all duration-500 ease-dreamy"
                            style={{
                                width: "calc(100% / 3)",
                                right:
                                    active === "morning"
                                        ? "0"
                                        : active === "evening"
                                        ? "calc(100% / 3)"
                                        : "calc(200% / 3)",
                            }}
                            aria-hidden
                        />
                        {tabs.map((t) => {
                            const isActive = active === t.id;
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => switchTo(t.id)}
                                    className={`tap-pop relative z-10 py-2 rounded-full text-sm font-semibold transition-colors duration-500 ${
                                        isActive
                                            ? "text-slate-900"
                                            : "text-slate-600 hover:text-slate-800"
                                    }`}
                                >
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                </nav>

                {/* Cards scroll region */}
                <main className="flex-1 overflow-y-auto scroll-region pr-1 pb-6">
                    <div className="flex flex-col gap-3">{azkarMarkup}</div>
                </main>
            </div>
        </div>
    );
}

export default App;
